import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent } from "../../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../components/ui/alert-dialog";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const emptyForm = { name: "", email: "", password: "", send_email: true };
const btn = "bg-[#090708] text-[#E8C47A] rounded-[2px] hover:bg-[#6F3F55] transition-colors";

const AdminRow = ({ a, isMe, onDelete }) => (
  <tr className="border-t border-[#9B6179]/5" data-testid={`admin-row-${a.email}`}>
    <td className="px-4 py-3">{a.name}{isMe && <span className="ml-2 text-xs text-[#9B6179]">(você)</span>}</td>
    <td className="px-4 py-3">{a.email}</td>
    <td className="px-4 py-3">
      {a.must_change_password
        ? <span className="text-xs px-2 py-1 bg-[#E8C47A]/20 text-[#6F3F55] rounded-[2px]" data-testid={`admin-status-${a.email}`}>Convite pendente</span>
        : <span className="text-xs px-2 py-1 bg-[#9B6179]/10 text-[#6F3F55] rounded-[2px]" data-testid={`admin-status-${a.email}`}>Ativo</span>}
    </td>
    <td className="px-4 py-3 text-xs text-[#8A7980]">{a.created_by || "—"}</td>
    <td className="px-4 py-3 text-right">
      {!isMe && <button onClick={() => onDelete(a)} data-testid={`delete-admin-${a.email}`}><Trash2 className="w-4 h-4 text-[#6F3F55] hover:text-red-700" /></button>}
    </td>
  </tr>
);

export default function Team() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [toDelete, setToDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/auth/admins").then((r) => setAdmins(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/auth/admins", form);
      if (data.email_sent === false) toast.warning("Admin criado, mas o e-mail de boas-vindas não pôde ser enviado.");
      else toast.success(data.email_sent ? "Admin criado e e-mail de boas-vindas enviado!" : "Admin criado!");
      setOpen(false); setForm(emptyForm); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setSaving(false);
  };

  const confirmDelete = async () => {
    try { await api.delete(`/auth/admins/${toDelete.id}`); toast.success("Admin excluído"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setToDelete(null);
  };

  return (
    <div data-testid="admin-team-page">
      <h1 className="text-2xl font-serif text-[#9B6179] mb-6">Equipe</h1>
      <div className="flex justify-end mb-4"><Button onClick={() => setOpen(true)} data-testid="new-admin-button" className={btn}><Plus className="w-4 h-4 mr-1" /> Novo Admin</Button></div>
      <div className="bg-white rounded-[2px] shadow-sm border border-[#9B6179]/5 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F0E8] text-[#6F3F55] text-xs uppercase"><tr><th className="text-left px-4 py-3">Nome</th><th className="text-left px-4 py-3">E-mail</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Criado por</th><th></th></tr></thead>
          <tbody>{admins.map((a) => <AdminRow key={a.id} a={a} isMe={a.id === user?.id} onDelete={setToDelete} />)}</tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="admin-modal">
          <form onSubmit={submit} className="space-y-3">
            <p className="font-serif text-lg text-[#9B6179]">Novo Admin</p>
            <div><Label>Nome</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-form-name" /></div>
            <div><Label>E-mail</Label><Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-form-email" /></div>
            <div><Label>Senha provisória (mín. 8 caracteres)</Label><Input required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="admin-form-password" /></div>
            <p className="text-xs text-[#8A7980]">Transmita esta senha você mesma. Ela deverá ser alterada no primeiro acesso.</p>
            <div className="flex items-center gap-2"><Switch checked={form.send_email} onCheckedChange={(v) => setForm({ ...form, send_email: v })} data-testid="admin-form-send-email" /><Label>Enviar e-mail de boas-vindas (sem a senha)</Label></div>
            <Button type="submit" disabled={saving} className={`w-full ${btn}`} data-testid="admin-form-submit">{saving ? "Criando..." : "Criar admin"}</Button>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-admin-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir este administrador?</AlertDialogTitle>
            <AlertDialogDescription>{toDelete?.name} ({toDelete?.email}) perderá o acesso ao back-office.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-admin-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="delete-admin-confirm" className="bg-red-700 hover:bg-red-800">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
